/**
  Author: Kenton Jones
  Last Edit: Aug 22 2025
  Contact: kenton.jones@cabindepot.com

 * Finds all instances of the registered trademark symbol (®) on a webpage
 * and wraps them in a <sup> tag. It handles dynamically loaded content and
 * avoids re-wrapping symbols that are already superscripted.
 */
(() => {
  // Symbol to be superscripted.
  const SYMBOL = '®';

  /**
   * Recursively traverses a DOM node to find and replace the target symbol.
   * @param {Node} startNode - The DOM node to start the traversal from.
   */
  const processNodeTree = (startNode) => {
    // Recursive DOM walker function.
    const walk = (node) => {
      try {
        // Process text nodes (nodeType 3).
        if (node.nodeType === 3) {
          if (node.nodeValue && node.nodeValue.includes(SYMBOL)) {
            const parent = node.parentNode;

            // Skip if the symbol is already inside a <sup> tag.
            if (parent && parent.nodeName === 'SUP') {
              return;
            }

            // Use a DocumentFragment for efficient DOM manipulation.
            const fragment = document.createDocumentFragment();
            const textParts = node.nodeValue.split(SYMBOL);

            // Split the text by the symbol and reconstruct with <sup> tags.
            textParts.forEach((part, index) => {
              fragment.appendChild(document.createTextNode(part));
              if (index < textParts.length - 1) {
                const sup = document.createElement('sup');
                sup.textContent = SYMBOL;
                fragment.appendChild(sup);
              }
            });

            // Replace the original text node with the new fragment.
            if (parent) {
              parent.replaceChild(fragment, node);
            }
          }
        }
        // Recursively process element nodes (nodeType 1), avoiding specific tags.
        else if (node.nodeType === 1 && node.nodeName !== 'SCRIPT' && node.nodeName !== 'STYLE' && node.nodeName !== 'TEXTAREA') {
          Array.from(node.childNodes).forEach(walk);
        }
      } catch (error) {
        console.error('Error processing node for superscript:', error, node);
      }
    };

    walk(startNode);
  };

  /**
   * Initializes a MutationObserver to process dynamically added content.
   */
  const observeDOMChanges = () => {
    // Callback function to execute when DOM mutations are observed.
    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => processNodeTree(node));
        }
      }
    });

    // Start observing the document body for child node additions.
    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }
  };

  // Initial execution on page load.
  document.addEventListener('DOMContentLoaded', () => {
    if (document.body) {
      // Process nodes present on initial load.
      processNodeTree(document.body);
      // Observe for future DOM changes.
      observeDOMChanges();
    } else {
      console.error('Superscript Script: document.body not found on DOMContentLoaded.');
    }
  });
})();
